import React from "react";
import { Link } from "react-router-dom";
import { Search, BookOpen, X, Highlighter, CheckSquare, HelpCircle } from "lucide-react";
import VersionToggle from "@/components/VersionToggle";

export default function Header({
    chapter, onOpenPicker, onOpenSearch, searchOpen,
    version, onVersionChange,
    selectMode, onToggleSelectMode,
    selectionCount = 0,
    showQuestions, onToggleShowQuestions,
}) {
    return (
        <header
            data-testid="app-header"
            className="sticky top-0 z-40 bg-sage-paper/85 backdrop-blur-xl border-b border-ink-10"
        >
            <div className="px-5 md:px-8 py-2 md:py-3 flex items-center justify-between gap-2">
                <button
                    data-testid="open-chapter-picker"
                    onClick={onOpenPicker}
                    className="flex items-center gap-2 group lg:cursor-default min-h-[44px] px-2 -mx-2 rounded-lg"
                    aria-label={`Matthew chapter ${chapter}`}
                >
                    <BookOpen size={16} className="text-moss-deep" />
                    <span className="font-ui font-semibold text-ink tracking-tight text-[15px]">
                        Matthew
                        <span className="ml-1.5 font-ui font-normal text-ink-70">{chapter}</span>
                    </span>
                    <span className="ml-1 text-ink-40 text-xs group-hover:text-ink-70 transition lg:hidden">▾</span>
                </button>
                <div className="flex items-center gap-1 md:gap-1.5">
                    {version && onVersionChange && (
                        <VersionToggle value={version} onChange={onVersionChange} />
                    )}

                    {onToggleShowQuestions && (
                        <IconLabelButton
                            testid="toggle-questions"
                            onClick={onToggleShowQuestions}
                            active={!!showQuestions}
                            ariaPressed={!!showQuestions}
                            title={showQuestions ? "Hide question markers" : "Show question markers"}
                            icon={HelpCircle}
                            label="Questions"
                            activeTint="moss"
                        />
                    )}

                    {onToggleSelectMode && (
                        <IconLabelButton
                            testid="toggle-select-mode"
                            onClick={onToggleSelectMode}
                            active={!!selectMode}
                            ariaPressed={!!selectMode}
                            title="Select verses"
                            icon={CheckSquare}
                            label="Select"
                            badge={selectionCount > 0 ? selectionCount : null}
                            badgeTestid="selection-count-badge"
                            activeTint="filled-moss"
                        />
                    )}

                    <IconLabelButton
                        as={Link}
                        to="/highlights"
                        testid="open-highlights"
                        title="Highlights"
                        icon={Highlighter}
                        label="Marks"
                    />

                    <button
                        data-testid="toggle-search"
                        onClick={onOpenSearch}
                        className="ml-0.5 w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center hover:bg-ink-10 transition"
                        aria-label={searchOpen ? "Close search" : "Open search"}
                    >
                        {searchOpen ? <X size={18} className="text-ink" /> : <Search size={18} className="text-ink" />}
                    </button>
                </div>
            </div>
        </header>
    );
}

/**
 * Icon-with-label header action.  Displays a small icon over a 10px caption so
 * every button has a written meaning while still fitting in mobile widths.
 *
 * activeTint:
 *   "moss"        — text turns moss-deep when active (soft toggle, used for Questions)
 *   "filled-moss" — background turns moss + white text when active (hard toggle, used for Select)
 */
function IconLabelButton({
    as: Comp = "button",
    testid,
    onClick,
    to,
    icon: Icon,
    label,
    ariaPressed,
    title,
    active = false,
    activeTint,
    badge = null,
    badgeTestid,
}) {
    const isFilled = active && activeTint === "filled-moss";
    const isSoft = active && activeTint === "moss";

    let cls = "relative inline-flex flex-col items-center justify-center gap-0.5 w-11 md:w-14 h-11 md:h-12 rounded-lg px-1 transition ";
    if (isFilled) cls += "bg-moss text-white shadow-sm ";
    else if (isSoft) cls += "text-moss-deep hover:bg-moss/10 ";
    else cls += "text-ink-70 hover:text-ink hover:bg-ink-10 ";

    const iconEl = <Icon size={16} strokeWidth={2} />;
    const labelEl = (
        <span className="font-ui text-[9px] md:text-[10px] font-medium leading-none tracking-[0.02em]">
            {label}
        </span>
    );

    const inner = (
        <>
            {iconEl}
            {labelEl}
            {badge != null && (
                <span
                    data-testid={badgeTestid}
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-plum text-white font-ui text-[10px] font-semibold leading-none flex items-center justify-center ring-2 ring-sage-paper"
                >
                    {badge}
                </span>
            )}
        </>
    );

    if (Comp === Link) {
        return (
            <Link
                to={to}
                data-testid={testid}
                title={title}
                aria-label={title}
                className={cls}
            >
                {inner}
            </Link>
        );
    }
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            aria-pressed={ariaPressed}
            aria-label={title}
            title={title}
            className={cls}
        >
            {inner}
        </button>
    );
}
